/*
 * Nether: mobile gallery pager v3 — the Gymshark morph.
 *
 * One dot per photo inside a floating pill. Each dot carries `--dot`, a 0..1 activation
 * driven by the horizontal scroll position of the mobile carousel (<slideshow-slides>):
 * 1 at the photo's resting position, falling off linearly to 0 one photo away. CSS maps
 * activation to width (dot -> wide dash) and opacity, so during a swipe the outgoing
 * dash shrinks exactly as the incoming one grows — the width visibly transfers.
 *
 * Uses ONE document-level capture scroll listener rather than per-element listeners, so it
 * survives Horizon's variant-change section morph (which can replace the scroller node).
 * scroll doesn't bubble, so capture is required to catch it at the document.
 */
(function () {
  'use strict';

  function clamp01(v) {
    return v < 0 ? 0 : v > 1 ? 1 : v;
  }

  function update(gallery) {
    if (!gallery) return;
    var prog = gallery.querySelector('[data-media-progress]');
    if (!prog) return;
    // Only VISIBLE dots count — per-color filtering hides slides + their dots.
    var dots = Array.prototype.filter.call(
      prog.querySelectorAll('.media-gallery__dot'),
      function (d) { return !d.hidden; }
    );
    var scroller = gallery.querySelector('slideshow-slides');
    if (!dots.length || !scroller) return;

    var max = scroller.scrollWidth - scroller.clientWidth;
    var ratio = max > 0 ? clamp01(scroller.scrollLeft / max) : 0;
    var n = dots.length;
    // Scroll spans photo 0 -> photo n-1. `position` is that float; a dot's activation is
    // 1 at its own index, 0 one photo away. The falloff is QUADRATIC (1 - d^2), not
    // linear: frame-by-frame of Gymshark's pill shows both dashes holding width
    // mid-swipe (outgoing resists shrinking, incoming swells early) - that overlap
    // is the tactile weight a linear transfer lacks (user 07-25).
    var position = ratio * (n - 1);
    for (var i = 0; i < n; i++) {
      var d = Math.min(1, Math.abs(position - i));
      dots[i].style.setProperty('--dot', (1 - d * d).toFixed(3));
    }
  }

  function updateAll(root) {
    (root || document).querySelectorAll('media-gallery').forEach(update);
  }

  // iOS Safari fires scroll events SPARSELY while a finger pans a snap
  // container - event-driven updates run seconds behind the thumb (user
  // iPhone test 07-25). So the pager is driven by a rAF loop that reads
  // scrollLeft directly every frame while there is any activity, and goes
  // to sleep after ~1.5s of stillness.
  var polling = new WeakSet();
  function startPoll(gallery) {
    if (!gallery || polling.has(gallery)) return;
    polling.add(gallery);
    var idle = 0;
    var last = -1;
    function tick() {
      var scroller = gallery.querySelector('slideshow-slides');
      if (!scroller || !gallery.isConnected) { polling.delete(gallery); return; }
      var x = scroller.scrollLeft;
      if (x !== last) {
        last = x;
        idle = 0;
        update(gallery);
      } else if (++idle > 90) {
        polling.delete(gallery);
        return;
      }
      requestAnimationFrame(tick);
    }
    requestAnimationFrame(tick);
  }

  document.addEventListener(
    'touchstart',
    function (e) {
      var g = e.target && e.target.closest && e.target.closest('media-gallery');
      if (g) startPoll(g);
    },
    { capture: true, passive: true }
  );

  // Scroll events still arm the loop (desktop wheel, programmatic jumps).
  document.addEventListener(
    'scroll',
    function (e) {
      var target = e.target;
      if (target && target.tagName === 'SLIDESHOW-SLIDES') {
        var gallery = target.closest('media-gallery');
        update(gallery);
        startPoll(gallery);
      }
    },
    true
  );

  document.addEventListener('DOMContentLoaded', function () { updateAll(document); });
  document.addEventListener('shopify:section:load', function (e) { updateAll(e.target); });
  window.addEventListener('resize', function () { updateAll(document); }, { passive: true });

  // ---- Per-color gallery filtering (Shopify has no per-variant gallery).
  // Media alt-tagged "[Black] ..." shows only while Black is selected; untagged
  // media shows for every color. Applies to carousel slides, desktop grid items
  // and pager dots (all stamped with data-media-color server-side). If a color
  // has no tagged media at all, everything stays visible - safe by default.
  // Prefer the canonical KEY over the raw option value.
  //
  // data-colorway-key is normalised through snippets/nether-colorway-key.liquid,
  // and every image's data-media-color is normalised through the same table, so
  // the two sides always agree on what a colour is called. That is what lets
  // "[Navy]" in an alt tag match a swatch whose option value is "Abyss" —
  // Shopify's own metaobject handle for that colour is literally "navy", so both
  // words are legitimate and the owner should not have to remember which.
  //
  // The fallback to data-colorway-value keeps older markup working. It compares
  // raw text, which is the behaviour that failed silently: a tag that did not
  // match exactly left NO image matching, and the guard below then unhid every
  // image, so a typo looked identical to a working tag.
  function selectedColor() {
    var dot = document.querySelector('.nether-colorway__dot.is-selected[data-colorway-key], .nether-colorway__dot.is-selected[data-colorway-value]');
    if (!dot) return '';
    var key = dot.getAttribute('data-colorway-key');
    if (key && key.trim()) return key.trim().toLowerCase();
    return (dot.getAttribute('data-colorway-value') || '').trim().toLowerCase();
  }

  function filterGallery() {
    var selected = selectedColor();
    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      // Per-gallery copy: mutating the shared variable made one no-match
      // gallery unhide every gallery after it in the loop.
      var color = selected;
      var tagged = gallery.querySelectorAll('[data-media-color]:not([data-media-color=""])');
      if (!tagged.length) return;
      if (color) {
        var anyForColor = Array.prototype.some.call(tagged, function (el) {
          return el.getAttribute('data-media-color') === color;
        });
        if (!anyForColor) color = ''; // tags exist but none match -> show all
      }
      gallery.querySelectorAll('[data-media-color]').forEach(function (el) {
        var mc = el.getAttribute('data-media-color');
        el.hidden = !!(color && mc && mc !== color);
      });
      // Land on the first visible slide — INSTANTLY — then let the pager recount.
      //
      // Instant on purpose (owner 2026-09-10: "it still loads the second one,
      // like, the model one instead of the first ghost"). The carousel scrolls
      // smoothly, so a plain scrollLeft assignment made from photo 3 rewound
      // the strip THROUGH photo 2 on its way to photo 1 — recorded on the draft
      // as the ghost-back at +80ms, the model at +160ms, the ghost at +240ms.
      // An inline scroll-behavior of auto makes this one jump instant and hands
      // the carousel its smooth scrolling straight back.
      //
      // This filter runs on the OLD gallery, before Horizon's replacement
      // arrives from the server, which is what makes a colour change feel
      // instant. It only works because the gallery block's "hide other
      // variants' media" (hide_variants) is OFF on templates/product.multicolor
      // .json. With it on, Horizon leaves every non-selected colour's featured
      // image — each colour's ghost front — out of the page entirely, so this
      // had no ghost to land on and fell to the model shot, and
      // nether-gallery-preload.js could not warm the ghost before the tap.
      // Keep it off; the colour filter already does that job.
      var scroller = gallery.querySelector('slideshow-slides');
      if (scroller) {
        var first = scroller.querySelector('slideshow-slide:not([hidden])');
        if (first) {
          var behavior = scroller.style.scrollBehavior;
          scroller.style.scrollBehavior = 'auto';
          scroller.scrollLeft = first.offsetLeft;
          scroller.style.scrollBehavior = behavior;
        }
      }
      update(gallery);
    });
  }

  // Mobile: tapping a colour takes you back up to the gallery.
  //
  // On a phone the swatches sit well below the fold, so changing colour
  // re-filtered a gallery the shopper could not see and the tap read as doing
  // nothing. Desktop is deliberately left alone: the gallery already sits beside
  // the buy column there, so scrolling would move something already in view.
  function revealGallery() {
    if (window.matchMedia('(min-width: 750px)').matches) return;
    // Not while the buy sheet is open (2026-09-25). The colour was picked in
    // the sheet, over the page, and the page behind it is meant to stay
    // exactly where it was (owner: "if I choose a color on the quick sheet it
    // scrolls back up to the gallery and that looks a little weird").
    if (document.documentElement.classList.contains('nether-sheet-open')) return;
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    // Clear whatever chrome is pinned to the top, or the gallery lands beneath
    // it. Measured rather than assumed: the announcement bar can be dismissed
    // and the header collapses on scroll, so the offset is not a constant.
    var pinned = 0;
    Array.prototype.forEach.call(
      document.querySelectorAll('header, nether-announcement, [data-sticky-header]'),
      function (el) {
        var pos = getComputedStyle(el).position;
        if (pos !== 'fixed' && pos !== 'sticky') return;
        var r = el.getBoundingClientRect();
        if (r.top <= 1 && r.bottom > pinned) pinned = r.bottom;
      }
    );

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var top = window.pageYOffset + gallery.getBoundingClientRect().top - pinned - 8;
    window.scrollTo({ top: top < 0 ? 0 : top, behavior: reduced ? 'auto' : 'smooth' });
  }

  var filterTimer = 0;
  var pendingReveal = false;

  // `reveal` is compared against literal true ON PURPOSE. Every listener below
  // is registered bare, so the browser hands this function an Event object, and
  // an Event is truthy — a plain `if (reveal)` would scroll the page to the
  // gallery on first load and on every section render in the editor.
  function queueFilter(reveal) {
    if (reveal === true) pendingReveal = true;
    clearTimeout(filterTimer);
    filterTimer = setTimeout(function () {
      filterGallery();
      // After the filter, never before: the reveal is only worth anything once
      // the gallery is already showing the new colour's first photo.
      if (pendingReveal) {
        pendingReveal = false;
        revealGallery();
      }
    }, 80);
  }

  document.addEventListener('DOMContentLoaded', queueFilter);
  document.addEventListener('shopify:section:load', queueFilter);
  document.addEventListener('variant:selected', queueFilter, true);
  document.addEventListener('shopify:product:select', function (e) {
    if (e && e.promise && typeof e.promise.then === 'function') {
      e.promise.then(queueFilter, queueFilter);
    } else {
      queueFilter();
    }
  }, true);
  // The colorway dots update optimistically on tap - catch that too. This is
  // the ONLY caller that passes true, because it is the only one that is
  // certainly a person choosing a colour rather than the page settling.
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.nether-colorway__dot[data-colorway-value]')) {
      queueFilter(true);
    }
  });
})();
